import { useMemo } from "react";

import type { FilterType } from "@/components/@types";
import { FilterChip } from "@/components/organisms/UtilityBar/FilterChip";
import { FilterChipPopper } from "@/components/organisms/UtilityBar/FilterChip/FilterChipPopper";
import { FilterChipStateProvider } from "@/components/organisms/UtilityBar/FilterChip/FilterChipStateContext";
import { editableSharedFilters } from "@/app/share/report/sharedReportFilters";
import { useSharedWidgetFilterDefinitions } from "@/app/app/_/views/ReportsView/reportFilterDefinitions.hooks";
import type { SharedViewWidgetShell } from "@/app/share/report/sharedReportTypes";
import { sharedFilterIsLocked } from "@/app/share/report/sharedReportFilters";

export function SharedWidgetFilters({
  widget,
  filters,
  onChange,
}: {
  widget: SharedViewWidgetShell;
  filters: FilterType[];
  onChange: (filters: FilterType[]) => void;
}) {
  const allDefinitions = useSharedWidgetFilterDefinitions({
    source: widget.source,
    filterDefinitions: widget.filterDefinitions ?? [],
  });
  const filterDefinitions = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(allDefinitions).filter(([key]) => {
          const stored = widget.defaultFilters?.find(
            (filter) => filter.key === key,
          );
          return !stored || !sharedFilterIsLocked(stored);
        }),
      ),
    [allDefinitions, widget.defaultFilters],
  );
  const editableFilters = useMemo(
    () => editableSharedFilters(filters),
    [filters],
  );

  if (Object.keys(filterDefinitions).length === 0) {
    return null;
  }

  return (
    <FilterChipStateProvider
      filters={editableFilters}
      filterDefinitions={filterDefinitions}
      onFiltersChange={onChange}
    >
      <div className="flex justify-end overflow-x-auto overflow-y-visible min-w-0 max-w-full [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex flex-row flex-nowrap items-center justify-end gap-1 w-max min-w-min">
          {editableFilters.map((filter, filterIndex) => (
            <FilterChip
              key={filter.key}
              filterKey={filter.key}
              filter={filter}
              filterIndex={filterIndex}
              compact
            />
          ))}
          <FilterChipPopper />
        </div>
      </div>
    </FilterChipStateProvider>
  );
}
